import { motion } from "framer-motion";
import { Bot, User, Volume2, VolumeX } from "lucide-react";
import { cn } from "../../lib/utils";

/**
 * إزالة تنسيق Markdown من النص
 */
const removeMarkdown = (text) => {
  if (!text) return text;
  return text
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*\*(.*?)\*\*\*/g, '$1')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/___(.*?)___/g, '$1')
    .replace(/__(.*?)__/g, '$1')
    .replace(/_(.*?)_/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/`{3}[\s\S]*?`{3}/g, '')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^[\s]*[-*+]\s+/gm, '')
    .replace(/^[\s]*\d+\.\s+/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

/**
 * معالجة النص لـ RTL
 */
const processText = (text) => {
  const arabicNumerals = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  let result = text;
  arabicNumerals.forEach((arabic, index) => {
    result = result.replace(new RegExp(arabic, 'g'), index.toString());
  });
  
  const lines = result.split('\n');
  const fixedLines = lines.map(line => {
    const match = line.match(/^(\d+)-\s*(.+)$/);
    if (match) return `${match[2]} -${match[1]}`;
    return line;
  });
  
  return fixedLines.join('\n');
};

/**
 * MessageItem - فقاعة الرسالة
 * @param {Object} message - بيانات الرسالة
 * @param {number} index - ترتيب الرسالة
 * @param {Function} onSpeak - دالة تشغيل الصوت
 * @param {string} speakingId - ID الرسالة قيد التشغيل
 * @param {Function} t - دالة الترجمة
 */
export function MessageItem({ message, index, onSpeak, speakingId, t }) {
  const isUser = message.role === "user";
  const cleanContent = isUser ? message.content : removeMarkdown(message.content);
  const isSpeaking = speakingId === message.id;
  const displayContent = processText(cleanContent);

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05, ease: "easeOut" }}
      className={cn(
        "flex items-start gap-3 py-3 w-full",
        isUser ? "justify-end" : "justify-start"
      )}
      data-testid={`message-${message.role}`}
    >
      {isUser ? (
        <>
          <div className="flex flex-col gap-1.5 items-end max-w-[85%]">
            {message.image && (
              <img 
                src={message.image} 
                alt="Attached" 
                className="max-w-[250px] rounded-xl border border-border"
              />
            )}
            <div className="bg-primary text-primary-foreground px-5 py-3.5 rounded-2xl rounded-tr-md shadow-sm">
              <p className="whitespace-pre-wrap leading-relaxed text-[15px]" dir="auto">
                {displayContent}
              </p>
            </div>
          </div>
          <motion.div 
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-primary text-primary-foreground"
          >
            <User className="w-5 h-5" />
          </motion.div>
        </>
      ) : (
        <>
          <motion.div 
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-secondary text-muted-foreground"
          >
            <Bot className="w-5 h-5" />
          </motion.div>
          <div className="flex flex-col gap-2 items-start flex-1 max-w-[90%]">
            <div className="text-foreground">
              <p className="whitespace-pre-wrap leading-[1.8] text-[15px]" dir="auto">
                {displayContent}
              </p>
            </div>
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              onClick={() => onSpeak(message.id, cleanContent)}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-all",
                isSpeaking 
                  ? "bg-green-500/10 text-green-600" 
                  : "hover:bg-secondary text-muted-foreground hover:text-foreground"
              )}
            >
              {isSpeaking ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
              <span>{isSpeaking ? t("stop") : t("listen")}</span>
            </motion.button>
          </div>
        </>
      )}
    </motion.div>
  );
}

export { removeMarkdown };
export default MessageItem;
